import { CheckIcon, LogOutIcon, MonitorIcon, MoonIcon, SunIcon } from "lucide-react"
import { useTheme } from "next-themes"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useAuth } from "@/features/auth/auth-context"
import { AccountSettings } from "@/features/preferences/AccountSettings"
import { TEXT_SIZES, useTextSize, type TextSize } from "@/features/preferences/text-size"
import { LOCALE_OPTIONS, setLocale, type Locale } from "@/i18n"

/**
 * The signed-in person's own settings, as a slide-over (§11.1, §11.4).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A SHEET RATHER THAN A PAGE, and it keeps the context the owner was in. He is
 * mid-way through a register or a bill when he decides the type is too small;
 * a sheet changes it and closes, leaving him exactly where he was. A page would
 * navigate him away and back.
 *
 * IT IS NOT DRIVEN BY A `SheetTrigger`. The demo wires trigger and content
 * together, which is right when the button is next to the sheet — but this one
 * opens from a DROPDOWN MENU ITEM, and the menu unmounts its own contents when
 * it closes. A `SheetTrigger` living inside `DropdownMenuContent` would be torn
 * down in the same frame that opened the sheet, so the sheet would flash and
 * vanish. `open`/`onOpenChange` are passed in from a parent that outlives the
 * menu instead.
 *
 * THE SHEET HAS TWO HALVES, AND THE SEPARATOR IS A REAL BOUNDARY. Language,
 * text size and theme are LOCAL TO THIS DEVICE — the same account on the office
 * desktop keeps its own — and they save to `localStorage`, instantly, with no
 * request and nothing to fail. Below the separator is `AccountSettings`, which
 * talks to `/api/v1/profile`: a name, a secret and a sign-in identifier are
 * facts about the PERSON, not about the phone in his hand, and they must follow
 * him to whatever device he picks up. That half asks for the current secret and
 * has a Save button for exactly the same reason — it can be refused.
 *
 * That lower half used to be an alert reading "not here yet — ask your son".
 *
 * NO CAPABILITY GATE ANYWHERE HERE. `settings.write` is ADMIN-only and covers
 * the GSTIN, the bank details and the tax master. The OWNER holds none of it —
 * and he is exactly the person who needs larger type and a Marathi interface.
 * His own preferences must not sit behind a door he cannot open (§2.1).
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function ProfileSheet({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}): React.JSX.Element {
  const { t, i18n } = useTranslation()
  const { user, signOut } = useAuth()
  const { theme, setTheme } = useTheme()
  const { size, setSize } = useTextSize()

  const currentLocale = i18n.language.split("-")[0] as Locale

  const sizeLabels: Record<TextSize, string> = {
    normal: t("pref.textNormal"),
    large: t("pref.textLarge"),
    larger: t("pref.textLarger"),
  }

  const themes = [
    { value: "light", label: t("pref.themeLight"), icon: <SunIcon /> },
    { value: "dark", label: t("pref.themeDark"), icon: <MoonIcon /> },
    { value: "system", label: t("pref.themeSystem"), icon: <MonitorIcon /> },
  ] as const

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-0">
        <SheetHeader>
          <SheetTitle>{t("pref.title")}</SheetTitle>
          <SheetDescription>{t("pref.subtitle")}</SheetDescription>
        </SheetHeader>

        {/* `overflow-y-auto` so a larger text size cannot push the footer off
          * the screen — the very setting most likely to make this list taller
          * is the one inside it. */}
        <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-4 py-2">
          {/* ── Who is signed in ─────────────────────────────────────────── */}
          <section className="flex flex-col gap-2">
            <Label className="text-muted-foreground text-xs">{t("pref.accountTitle")}</Label>
            <div className="flex items-center justify-between gap-3">
              <span className="font-medium">{user?.displayName ?? ""}</span>
              {/*
                * The ROLE, not an invented email — `AuthUser` carries id, role
                * and displayName only. When a father and son share a tablet
                * this is the more useful fact: it explains why a button is or
                * is not there.
                */}
              <Badge variant="outline">
                {user === null ? "" : t(`role.${user.role.toLowerCase()}`)}
              </Badge>
            </div>
          </section>

          <Separator />

          {/* ── Language (§11.4) ─────────────────────────────────────────── */}
          <section className="flex flex-col gap-2">
            <Label>{t("lbl.language")}</Label>
            <p className="text-muted-foreground text-xs">{t("pref.languageHelp")}</p>
            <div className="flex flex-wrap gap-2">
              {LOCALE_OPTIONS.map((option) => (
                /*
                 * THE GLYPH LEADS and each language names itself in its OWN
                 * script. §11.4 wants this reachable "without reading English",
                 * so "Marathi" spelled in English would defeat the purpose for
                 * the one person it exists for.
                 */
                <Button
                  key={option.value}
                  variant={option.value === currentLocale ? "default" : "outline"}
                  onClick={() => {
                    void setLocale(option.value)
                  }}
                >
                  <span aria-hidden="true" className="text-base">
                    {option.glyph}
                  </span>
                  {option.label}
                  {option.value === currentLocale ? <CheckIcon className="size-4" /> : null}
                </Button>
              ))}
            </div>
          </section>

          {/* ── Text size (§11.1) ────────────────────────────────────────── */}
          <section className="flex flex-col gap-2">
            <Label>{t("pref.textSize")}</Label>
            <p className="text-muted-foreground text-xs">{t("pref.textSizeHelp")}</p>
            <div className="flex flex-wrap gap-2">
              {TEXT_SIZES.map((value) => (
                <Button
                  key={value}
                  variant={value === size ? "default" : "outline"}
                  onClick={() => {
                    setSize(value)
                  }}
                >
                  {sizeLabels[value]}
                  {value === size ? <CheckIcon className="size-4" /> : null}
                </Button>
              ))}
            </div>
          </section>

          {/* ── Appearance ───────────────────────────────────────────────── */}
          <section className="flex flex-col gap-2">
            <Label>{t("pref.theme")}</Label>
            <div className="flex flex-wrap gap-2">
              {themes.map((option) => (
                <Button
                  key={option.value}
                  variant={option.value === theme ? "default" : "outline"}
                  onClick={() => {
                    setTheme(option.value)
                  }}
                >
                  {option.icon}
                  {option.label}
                  {option.value === theme ? <CheckIcon className="size-4" /> : null}
                </Button>
              ))}
            </div>
          </section>

          <Separator />

          {/*
            * ═══════════════════════════════════════════════════════════════
            * THIS USED TO BE AN ALERT SAYING "NOT HERE YET — ask your son".
            *
            * It was honest at the time: `/auth/*` was owner-login, admin-login
            * and refresh, and every route that could change a secret sat behind
            * `user.manage`, which the OWNER does not hold. So the proprietor
            * could not rotate his own PIN, and the CA could not rotate her own
            * password without somebody inside the business choosing it for her.
            *
            * `/api/v1/profile` (`profile.manage`, held by all three roles) is
            * what closed that, and this section is the front of it. Everything
            * ABOVE this line is still a device preference that saves nowhere;
            * everything below talks to the server. The separator is the seam.
            * ═══════════════════════════════════════════════════════════════
            */}
          <AccountSettings />

          <p className="text-muted-foreground text-xs">
            {t("app.name")} {__APP_VERSION__}
          </p>
        </div>

        {/*
          * NO "SAVE" BUTTON FOR THE PREFERENCES, deliberately. Every control in
          * the upper half applies the moment it is tapped — the language
          * changes, the type grows, the theme flips. A Save button would imply
          * the changes had NOT yet taken effect while the owner was looking
          * straight at them having taken effect.
          *
          * THE ACCOUNT FORMS BELOW EACH HAVE THEIR OWN SUBMIT, and that is not
          * an inconsistency: a name goes to a server that can refuse it, and a
          * secret cannot be "applied as you type" without spending a bcrypt
          * comparison and a lockout attempt on every keystroke. Their buttons
          * say what they DO — "Change name", "Change PIN" — and never "Save",
          * so the two halves do not look like one mechanism behaving oddly.
          */}
        <SheetFooter>
          <Button variant="outline" onClick={signOut}>
            <LogOutIcon />
            {t("btn.signOut")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
